import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment'; // Added moment
import { Table, Spin, Alert, Button, Tag, Modal, Form, Input, Select, message, Popconfirm, Space, Row, Col, Typography, Tooltip, Checkbox } from 'antd'; // Added Checkbox, removed unused Card
import MomentDatePicker from './MomentDatePicker.tsx';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons'; // Removed unused MedicineBoxOutlined
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions'; // Already present

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography; // Added Title destructuring

// Helper to format date
const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
};

const PatientAllergyList = ({ patientId }) => {
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAllergy, setEditingAllergy] = useState(null); // null for Add, object for Edit
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [lastReviewed, setLastReviewed] = useState(null); // Added state
  const [nkda, setNkda] = useState(false); // NKDA status
  const [nkdaLoading, setNkdaLoading] = useState(false); // Loading state for NKDA updates
  const { user } = useAuth(); // For recordedBy and role checks
  const { hasPermission } = usePermissions();

  const fetchAllergies = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch patient data to get allergies and review status
      const response = await axios.get(`/patients/${patientId}?populate=allergyLastReviewed.user`);
      const patientData = response.data.data.patient;
      // Assuming allergies are still fetched separately or populated if needed
      // For now, let's assume allergies are part of patientData or fetched elsewhere if large
      // setAllergies(patientData?.allergies || []); // Adjust if allergies are nested
      setLastReviewed(patientData?.allergyLastReviewed || null); // Set review status

      // Fetch actual allergies separately if not populated (this now includes NKDA status)
      const allergyResponse = await axios.get(`/patients/${patientId}/allergies`);
      setAllergies(allergyResponse.data.data.allergies);
      setNkda(allergyResponse.data.data.nkda || false); // Set NKDA status from allergy response

    } catch (err) {
      console.error("Error fetching allergy data:", err);
      setError(err.response?.data?.message || 'Failed to fetch allergy data.');
      setLastReviewed(null); // Clear on error
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchAllergies();
  }, [fetchAllergies]);

  // Handle NKDA checkbox change
  const handleNkdaChange = async (checked) => {
    if (!canModify) return;

    setNkdaLoading(true);
    try {
      await axios.patch(`/patients/${patientId}/nkda`, { nkda: checked });
      setNkda(checked);
      message.success(`NKDA status ${checked ? 'enabled' : 'disabled'} successfully!`);
    } catch (err) {
      console.error("Error updating NKDA status:", err);
      message.error(err.response?.data?.message || 'Failed to update NKDA status.');
    } finally {
      setNkdaLoading(false);
    }
  };

  const showModal = (allergy = null) => {
    // Check if trying to add new allergy when NKDA is true
    if (!allergy && nkda) {
      message.warning('Cannot add allergies when No Known Drug Allergy (NKDA) is checked. Please uncheck NKDA first.');
      return;
    }

    setEditingAllergy(allergy);
    if (allergy) {
      // Pre-fill form for editing
      form.setFieldsValue({
        ...allergy,
        onsetDate: allergy.onsetDate ? moment(allergy.onsetDate) : null,
      });
    } else {
      // Reset form for adding
      form.resetFields();
      form.setFieldsValue({ status: 'Active', severity: 'Unknown', type: 'Other' }); // Defaults
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingAllergy(null);
    form.resetFields();
  };

  const handleFinish = async (values) => {
    if (!user || !user._id) {
        message.error('User not logged in.');
        return;
    }
    setSubmitLoading(true);
    setError(null);

    const payload = {
      ...values,
      onsetDate: values.onsetDate ? values.onsetDate.toISOString() : null,
      // recordedBy and patient are handled by backend/route
    };

    try {
      if (editingAllergy) {
        // Update existing allergy
        await axios.patch(`/allergies/${editingAllergy._id}`, payload); // Removed prefix
        message.success('Allergy updated successfully!');
      } else {
        // Create new allergy
        await axios.post(`/patients/${patientId}/allergies`, payload); // Removed prefix
        message.success('Allergy added successfully!');
      }
      handleCancel();
      fetchAllergies(); // Refresh list
    } catch (err) {
      console.error("Error saving allergy:", err);
      message.error(err.response?.data?.message || 'Failed to save allergy.');
    } finally {
      setSubmitLoading(false);
    }
  };

   const handleDelete = async (allergyId) => {
    try {
      await axios.delete(`/allergies/${allergyId}`); // Removed prefix
      message.success('Allergy deleted successfully!');
      fetchAllergies(); // Refresh list
    } catch (err) {
      console.error("Error deleting allergy:", err);
      message.error(err.response?.data?.message || 'Failed to delete allergy.');
    }
  };

  // --- Mark as Reviewed Logic ---
  const handleMarkReviewed = async () => {
    if (!user || !user._id) {
      message.error("Cannot mark as reviewed: User not logged in.");
      return;
    }
    const reviewData = { user: user._id, date: new Date().toISOString() };
    setLoading(true);
    try {
      await axios.patch(`/patients/${patientId}`, { allergyLastReviewed: reviewData });
      message.success('Allergies marked as reviewed.');
      setLastReviewed({ user: { _id: user._id, name: user.name }, date: reviewData.date });
    } catch (err) {
      console.error("Error marking allergies as reviewed:", err);
      message.error(err.response?.data?.message || 'Failed to mark as reviewed.');
    } finally {
      setLoading(false);
    }
  };


  const columns = [
    {
      title: 'Substance',
      dataIndex: 'substance',
      key: 'substance',
    },
    {
      title: 'Reaction',
      dataIndex: 'reaction',
      key: 'reaction',
    },
     {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filters: [
        { text: 'Drug', value: 'Drug' },
        { text: 'Food', value: 'Food' },
        { text: 'Environmental', value: 'Environmental' },
        { text: 'Other', value: 'Other' },
      ],
      onFilter: (value, record) => record.type.includes(value),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
       render: (severity) => {
        let color = 'default';
        if (severity === 'Life-threatening') color = 'error';
        else if (severity === 'Severe') color = 'warning';
        else if (severity === 'Moderate') color = 'processing';
        else if (severity === 'Mild') color = 'success';
        return <Tag color={color}>{severity}</Tag>;
      },
       filters: [
        { text: 'Mild', value: 'Mild' },
        { text: 'Moderate', value: 'Moderate' },
        { text: 'Severe', value: 'Severe' },
        { text: 'Life-threatening', value: 'Life-threatening' },
        { text: 'Unknown', value: 'Unknown' },
      ],
       onFilter: (value, record) => record.severity.includes(value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={status === 'Active' ? 'blue' : 'default'}>{status}</Tag>,
       filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Inactive', value: 'Inactive' },
      ],
      onFilter: (value, record) => record.status.includes(value),
    },
    {
      title: 'Onset Date',
      dataIndex: 'onsetDate',
      key: 'onsetDate',
      render: formatDate,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => showModal(record)} size="small">Edit</Button>
          <Popconfirm
            title="Are you sure you want to delete this allergy?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
             <Button icon={<DeleteOutlined />} danger size="small">Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Check if user has permission to add/edit/delete using new permission system
  const canModify = hasPermission('clinical.allergies.manage');

  return (
    <>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      {/* Standard Action Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '10px' }}>
        <Space>
          {canModify && (
            <Tooltip title={nkda ? "Cannot add allergies when NKDA is checked" : "Add new allergy"}>
              <Button
                icon={<PlusOutlined />}
                onClick={() => showModal(null)}
                disabled={nkda}
              >
                Add Allergy
              </Button>
            </Tooltip>
          )}
          {/* Added Mark as Reviewed Button */}
          {canModify && (
            <Tooltip title="Mark this section as reviewed by you right now">
              <Button icon={<CheckCircleOutlined />} onClick={handleMarkReviewed}>
                Mark as Reviewed
              </Button>
            </Tooltip>
          )}
        </Space>
        {/* Added Review Status Display */}
        <div style={{ textAlign: 'right' }}>
          {lastReviewed?.date ? (
            <Text type="secondary" style={{ fontSize: '0.85em' }}>
              Last Reviewed by {lastReviewed.user?.name || 'Unknown User'} on {moment(lastReviewed.date).format('L LT')}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: '0.85em' }}>Not yet reviewed</Text>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px', marginBottom: '10px' }}>
        <Title level={5} style={{ margin: 0 }}>Allergy List</Title>
        <Tooltip title={nkda ? "Patient has no known drug allergies" : "Check if patient has no known drug allergies"}>
          <Checkbox
            checked={nkda}
            onChange={(e) => handleNkdaChange(e.target.checked)}
            disabled={!canModify || nkdaLoading || (allergies.length > 0 && !nkda)}
            loading={nkdaLoading}
          >
            No Known Drug Allergy (NKDA)
          </Checkbox>
        </Tooltip>
      </div>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={allergies}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Spin>

      {/* Add/Edit Allergy Modal */}
      <Modal
        title={editingAllergy ? 'Edit Allergy' : 'Add New Allergy'}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Spin spinning={submitLoading}>
          <Form form={form} layout="vertical" onFinish={handleFinish}>
            <Form.Item
              name="substance"
              label="Substance"
              rules={[{ required: true, message: 'Substance is required.' }]}
            >
              <Input />
            </Form.Item>
             <Form.Item
              name="reaction"
              label="Reaction"
              rules={[{ required: true, message: 'Reaction is required.' }]}
            >
              <TextArea rows={2} />
            </Form.Item>
             <Row gutter={16}>
               <Col span={12}>
                 <Form.Item name="type" label="Type">
                   <Select>
                     <Option value="Drug">Drug</Option>
                     <Option value="Food">Food</Option>
                     <Option value="Environmental">Environmental</Option>
                     <Option value="Other">Other</Option>
                   </Select>
                 </Form.Item>
               </Col>
               <Col span={12}>
                 <Form.Item name="severity" label="Severity">
                   <Select>
                     <Option value="Mild">Mild</Option>
                     <Option value="Moderate">Moderate</Option>
                     <Option value="Severe">Severe</Option>
                     <Option value="Life-threatening">Life-threatening</Option>
                     <Option value="Unknown">Unknown</Option>
                   </Select>
                 </Form.Item>
               </Col>
             </Row>
             <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="status" label="Status">
                      <Select>
                        <Option value="Active">Active</Option>
                        <Option value="Inactive">Inactive</Option>
                      </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="onsetDate" label="Onset Date">
                      <MomentDatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
             </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingAllergy ? 'Save Changes' : 'Add Allergy'}
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default PatientAllergyList;
